import { useState, useEffect, useCallback, useMemo } from 'react';
import { BREAKPOINTS } from '../utils/constants';

export const useResponsiveCalendar = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowSize.width < BREAKPOINTS.MOBILE;
  const isTablet = windowSize.width >= BREAKPOINTS.MOBILE && windowSize.width < BREAKPOINTS.TABLET;
  const isDesktop = windowSize.width >= BREAKPOINTS.TABLET;

  const breakpoint = useMemo(() => {
    if (isMobile) return 'mobile';
    if (isTablet) return 'tablet';
    return 'desktop';
  }, [isMobile, isTablet]);

  const orientation = useMemo(() => {
    return windowSize.width > windowSize.height ? 'landscape' : 'portrait';
  }, [windowSize]);

  const calendarHeight = useMemo(() => {
    const headerOffset = isMobile ? 200 : 300;
    return Math.max(400, windowSize.height - headerOffset);
  }, [windowSize.height, isMobile]);

  const visibleDays = useMemo(() => {
    if (isMobile) return 1;
    if (isTablet) return 5;
    return 7;
  }, [isMobile, isTablet]);

  const shouldUseMobileView = useCallback(() => {
    return isMobile || (isTablet && orientation === 'portrait');
  }, [isMobile, isTablet, orientation]);

  return {
    windowSize,
    isMobile,
    isTablet,
    isDesktop,
    breakpoint,
    orientation,
    calendarHeight,
    visibleDays,
    shouldUseMobileView,
  };
};

export default useResponsiveCalendar;
